import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  walkiesClient,
  type AdminAutoCloseStaleConversationsResponseDto,
  type AdminStaleConversationDto,
} from "../api/walkiesClient";

type LoadState = "idle" | "loading" | "loaded" | "error";

function formatDateTime(value?: string | null): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildOwnerLabel(item: AdminStaleConversationDto): string {
  return item.ownerName?.trim() || item.ownerEmail;
}

const AdminConversationsPage: React.FC = () => {
  const [staleAfterDays, setStaleAfterDays] = useState<number>(30);
  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState("");
  const [items, setItems] = useState<AdminStaleConversationDto[]>([]);
  const [runningClose, setRunningClose] = useState(false);
  const [result, setResult] =
    useState<AdminAutoCloseStaleConversationsResponseDto | null>(null);

  const staleCount = useMemo(() => items.length, [items]);

  async function handlePreview(e?: React.FormEvent) {
    e?.preventDefault();

    try {
      setState("loading");
      setError("");
      setResult(null);

      const data = await walkiesClient.getAdminStaleConversationPreview(
        staleAfterDays
      );

      setItems(Array.isArray(data) ? data : []);
      setState("loaded");
    } catch (err) {
      setState("error");
      setError(
        err instanceof Error
          ? err.message
          : "Failed to preview stale conversations."
      );
    }
  }

  async function handleAutoClose() {
    try {
      setRunningClose(true);
      setError("");
      setResult(null);

      const response = await walkiesClient.autoCloseAdminStaleConversations(
        staleAfterDays
      );

      setResult(response);

      const refreshed = await walkiesClient.getAdminStaleConversationPreview(
        staleAfterDays
      );

      setItems(Array.isArray(refreshed) ? refreshed : []);
      setState("loaded");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to auto-close stale conversations."
      );
    } finally {
      setRunningClose(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <section style={styles.heroCard}>
          <div style={styles.heroTopRow}>
            <div style={styles.badge}>Admin</div>
            <div style={styles.heroActions}>
              <Link to="/walker/messages" style={styles.secondaryLinkButton}>
                Back to messages
              </Link>
              <Link to="/walker/dashboard" style={styles.secondaryLinkButton}>
                Dashboard
              </Link>
            </div>
          </div>

          <h1 style={styles.title}>Conversation admin</h1>
          <p style={styles.subtitle}>
            Preview stale conversations and manually archive them using the admin
            endpoint.
          </p>
        </section>

        <section style={styles.panelCard}>
          <form onSubmit={handlePreview} style={styles.formRow}>
            <div style={styles.field}>
              <label htmlFor="staleAfterDays" style={styles.label}>
                Stale after days
              </label>
              <input
                id="staleAfterDays"
                type="number"
                min={1}
                value={staleAfterDays}
                onChange={(e) =>
                  setStaleAfterDays(Math.max(1, Number(e.target.value) || 1))
                }
                style={styles.input}
              />
            </div>

            <div style={styles.formActions}>
              <button
                type="submit"
                style={styles.primaryButton}
                disabled={state === "loading"}
              >
                {state === "loading" ? "Loading preview..." : "Preview stale"}
              </button>

              <button
                type="button"
                style={{
                  ...styles.warnButton,
                  opacity: staleCount === 0 || runningClose ? 0.65 : 1,
                  cursor:
                    staleCount === 0 || runningClose ? "not-allowed" : "pointer",
                }}
                disabled={staleCount === 0 || runningClose}
                onClick={handleAutoClose}
              >
                {runningClose ? "Closing..." : "Run auto-close"}
              </button>
            </div>
          </form>

          {error && <div style={styles.errorBox}>{error}</div>}

          {result && (
            <div style={styles.successBox}>
              Auto-close completed. Rows affected: <strong>{result.rowsAffected}</strong>
            </div>
          )}

          <div style={styles.summaryRow}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Current preview count</div>
              <div style={styles.summaryValue}>{staleCount}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Threshold</div>
              <div style={styles.summaryValue}>{staleAfterDays} days</div>
            </div>
          </div>
        </section>

        <section style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h2 style={styles.sectionTitleDark}>Stale conversations</h2>
            <div style={styles.countBadge}>
              {staleCount} row{staleCount === 1 ? "" : "s"}
            </div>
          </div>

          {state === "idle" ? (
            <div style={styles.emptyBox}>
              Run a preview to see which active conversations would be archived.
            </div>
          ) : state === "loading" ? (
            <div style={styles.emptyBox}>Loading preview…</div>
          ) : staleCount === 0 ? (
            <div style={styles.emptyBox}>
              No stale active conversations were found for the selected threshold.
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Conversation</th>
                    <th style={styles.th}>Walker profile</th>
                    <th style={styles.th}>Owner</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Created</th>
                    <th style={styles.th}>Last message</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.conversationId}>
                      <td style={styles.td}>{item.conversationId}</td>
                      <td style={styles.td}>{item.walkerProfileId}</td>
                      <td style={styles.td}>
                        <div style={styles.ownerName}>{buildOwnerLabel(item)}</div>
                        <div style={styles.ownerEmail}>{item.ownerEmail}</div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.statusBadge}>{item.status}</span>
                      </td>
                      <td style={styles.td}>{formatDateTime(item.createdUtc)}</td>
                      <td style={styles.td}>{formatDateTime(item.lastMessageUtc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 64px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 1240,
    margin: "0 auto",
  },
  heroCard: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    marginBottom: 22,
  },
  heroTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  heroActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.12)",
    fontWeight: 800,
  },
  title: {
    margin: "0 0 10px",
    fontSize: 42,
    lineHeight: 1.02,
    letterSpacing: "-0.04em",
    fontWeight: 900,
  },
  subtitle: {
    margin: 0,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.82)",
  },
  panelCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
    marginBottom: 22,
  },
  formRow: {
    display: "flex",
    gap: 16,
    alignItems: "end",
    flexWrap: "wrap",
  },
  field: {
    display: "grid",
    gap: 8,
    minWidth: 220,
  },
  label: {
    fontWeight: 800,
    color: "#213a33",
  },
  input: {
    font: "inherit",
    padding: "12px 14px",
    borderRadius: 14,
    outline: "none",
    border: "1px solid rgba(24, 32, 29, 0.14)",
    background: "#ffffff",
    color: "#18201d",
    minWidth: 140,
  },
  formActions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    background: "#f28a16",
    color: "#ffffff",
  },
  warnButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 16,
    padding: "14px 18px",
    fontSize: 15,
    fontWeight: 800,
    background: "#8a2d1d",
    color: "#ffffff",
  },
  secondaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 800,
    background: "#213a33",
    color: "#ffffff",
    textDecoration: "none",
  },
  errorBox: {
    marginTop: 16,
    borderRadius: 16,
    padding: 14,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.14)",
    color: "#7f1d1d",
    fontWeight: 700,
  },
  successBox: {
    marginTop: 16,
    borderRadius: 16,
    padding: 14,
    background: "rgba(33, 58, 51, 0.08)",
    border: "1px solid rgba(33, 58, 51, 0.12)",
    color: "#213a33",
    fontWeight: 700,
  },
  summaryRow: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    marginTop: 18,
  },
  summaryCard: {
    minWidth: 180,
    borderRadius: 18,
    padding: 16,
    background: "rgba(24, 32, 29, 0.05)",
  },
  summaryLabel: {
    fontSize: 13,
    color: "rgba(24, 32, 29, 0.62)",
    marginBottom: 6,
    fontWeight: 700,
  },
  summaryValue: {
    fontSize: 28,
    fontWeight: 900,
    color: "#213a33",
  },
  tableCard: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 24,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  sectionTitleDark: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    color: "#213a33",
  },
  countBadge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(24, 32, 29, 0.06)",
    fontWeight: 800,
    color: "#213a33",
  },
  emptyBox: {
    borderRadius: 18,
    padding: 16,
    background: "rgba(24, 32, 29, 0.05)",
    color: "rgba(24, 32, 29, 0.72)",
    lineHeight: 1.7,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    borderBottom: "1px solid rgba(24, 32, 29, 0.12)",
    color: "#213a33",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  td: {
    padding: "14px 10px",
    borderBottom: "1px solid rgba(24, 32, 29, 0.08)",
    verticalAlign: "top",
    color: "rgba(24, 32, 29, 0.88)",
  },
  ownerName: {
    fontWeight: 800,
    color: "#213a33",
    marginBottom: 4,
  },
  ownerEmail: {
    fontSize: 13,
    color: "rgba(24, 32, 29, 0.6)",
  },
  statusBadge: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(242, 138, 22, 0.12)",
    border: "1px solid rgba(242, 138, 22, 0.22)",
    color: "#b45309",
    fontSize: 12,
    fontWeight: 800,
  },
};

export default AdminConversationsPage;